import { create } from 'zustand';
import { Venta, Producto, Cliente } from '../types';
import { supabase } from '../lib/supabase';
import { getCurrentEmpresaIdSync } from '../utils/empresaUtils';
import { generateUniqueNumero } from '../utils/supabaseHelpers';
import { handleStoreAction } from '../utils/storeHelpers';
import { logger } from '../utils/logger';

interface VentasState {
  ventas: Venta[];
  ventaSeleccionada: Venta | null;
  ventaEnProceso: Venta | null;
  isLoading: boolean;
  error: string | null;
  filtros: {
    estado: string;
    fechaDesde: string;
    fechaHasta: string;
    clienteId: string;
    metodoPago: string;
  };
  paginacion: {
    pagina: number;
    porPagina: number;
    total: number;
  };
  
  // Actions
  fetchVentas: (filtros?: any) => Promise<Venta[] | null | void>;
  fetchVenta: (id: string) => Promise<void>;
  crearVenta: (venta: Partial<Venta>) => Promise<Venta | null | void>;
  actualizarVenta: (id: string, venta: Partial<Venta>) => Promise<Venta | null | void>;
  anularVenta: (id: string) => Promise<void | null>;
  eliminarVenta: (id: string) => Promise<void | null>;
  agregarItemVenta: (itemData: { productoId: string; producto: Producto; cantidad: number; precioUnitario: number; descuento: number; total: number; costoUnitario: number; ivaPorcentaje: number }) => void;
  removerItemVenta: (productoId: string) => void;
  actualizarItemVenta: (productoId: string, cantidad: number, precio?: number) => void;
  calcularTotales: () => { subtotal: number; impuestos: number; total: number; rentabilidadReal: number } | undefined;
  finalizarVenta: (ventaData: { clienteId: string; metodoPago: string; notas?: string }) => Promise<void>;
  setVentaSeleccionada: (venta: Venta | null) => void;
  setVentaEnProceso: (venta: Venta | null) => void;
  setFiltros: (filtros: any) => void;
  setPaginacion: (paginacion: any) => void;
  clearError: () => void;
  reset: () => void;
  limpiarVentaEnProceso: () => void;
  obtenerVentaEnProceso: () => Venta | null;
  obtenerClienteVenta: (clienteId: string) => Cliente | null;
  obtenerProductoVenta: (productoId: string) => Producto | null;
  crearClienteRapido: (datosCliente: Partial<Cliente>) => Promise<Cliente>;
}

export const useVentasStore = create<VentasState>((set, get) => ({
  ventas: [],
  ventaSeleccionada: null,
  ventaEnProceso: null,
  isLoading: false,
  error: null,
  filtros: {
    estado: '',
    fechaDesde: '',
    fechaHasta: '',
    clienteId: '',
    metodoPago: ''
  },
  paginacion: {
    pagina: 1,
    porPagina: 20,
    total: 0
  },

  fetchVentas: async (filtrosParcial: Record<string, unknown> = {}) => {
    return handleStoreAction(
      async () => {
        const filtros = { ...get().filtros, ...filtrosParcial };
        logger.debug('🔍 Fetching ventas with filtros:', filtros);

        // Get current user from auth store
        const { useAuthStore } = await import('./useAuthStore');
        const authStore = useAuthStore.getState();
        const currentUser = authStore.usuario;

        const empresaId = getCurrentEmpresaIdSync();
        if (!currentUser || !empresaId || empresaId === 'sin-empresa') {
          logger.warn('❌ No authenticated user or empresa ID');
          set({
            ventas: [],
            paginacion: { ...get().paginacion, total: 0 }
          });
          return;
        }

        logger.debug('🏢 Fetching ventas for empresa ID:', empresaId);

      // Base: todas las ventas de la empresa; sin filtro de estado por defecto
      let query = supabase
        .from('ventas')
        .select('*')
        .eq('empresa_id', empresaId);

      const estadoVal = typeof filtros.estado === 'string' ? filtros.estado.trim() : '';
      if (estadoVal && ['PAGADA', 'PENDIENTE', 'ANULADA'].includes(estadoVal)) {
        query = query.eq('estado', estadoVal);
      }
      if (filtros.fechaDesde) {
        query = query.gte('fecha', filtros.fechaDesde);
      }
      if (filtros.fechaHasta) {
        query = query.lte('fecha', filtros.fechaHasta);
      }
      if (filtros.clienteId) {
        query = query.eq('cliente_id', filtros.clienteId);
      }
      if (filtros.metodoPago) {
        query = query.eq('metodo_pago', filtros.metodoPago);
      }

      query = query.order('created_at', { ascending: false });
      
        const { data: supabaseVentas, error: fetchError } = await query;

        if (fetchError) {
          logger.error('🔍 FETCH ERROR DETAILS:', {
            message: fetchError.message,
            details: fetchError.details,
            hint: fetchError.hint,
            code: fetchError.code
          });
          throw fetchError;
        }

        logger.debug('💰 Ventas cargadas desde Supabase:', supabaseVentas);

        // Fetch clients separately and create a lookup map
        const { data: clientes, error: clientesError } = await supabase
          .from('clientes')
          .select('id, nombre, apellido, telefono, email, numero_documento')
          .eq('empresa_id', empresaId);

        if (clientesError) {
          logger.error('❌ Error fetching clientes:', clientesError);
        }

        const clientesMap = new Map();
        if (clientes) {
          clientes.forEach(cliente => {
            clientesMap.set(cliente.id, cliente);
          });
        }

        logger.debug('👥 Clientes cargados para ventas:', clientesMap.size);
      
      // Convert to local format
      const ventasLocales = (supabaseVentas || []).map((v: any) => ({
        id: v.id,
        numero: v.numero,
        facturaId: v.factura_id ?? null,
        clienteId: v.cliente_id,
        cliente: v.cliente_id ? (() => {
          const cliente = clientesMap.get(v.cliente_id);
          return cliente ? {
            id: cliente.id,
            empresaId: v.empresa_id,
            nombre: cliente.nombre || 'Sin nombre',
            apellido: cliente.apellido || '',
            telefono: cliente.telefono || 'N/A',
            email: cliente.email || 'N/A',
            numeroDocumento: cliente.numero_documento || 'N/A',
            createdAt: new Date(v.created_at || new Date()),
            updatedAt: new Date(v.updated_at || new Date())
          } : undefined;
        })() : undefined,
        fecha: new Date(v.fecha),
        items: v.items ? (typeof v.items === 'string' ? JSON.parse(v.items) : v.items) : [],
        subtotal: v.subtotal || 0,
        impuestos: v.impuestos || 0,
        descuentos: v.descuentos || 0,
        total: v.total || 0,
        metodoPago: v.metodo_pago || '', // Fixed: Don't default to EFECTIVO
        estado: v.estado || 'PENDIENTE',
        vendedor: 'Vendedor Demo', // Default value since column doesn't exist
        notas: v.notas || '',
        rentabilidadReal: v.rentabilidad_real || 0, // Use actual value from database
        canalOrigen: v.canal_origen,
        fotoEntrega: v.foto_entrega ?? null,
        fotosEntrega: Array.isArray(v.fotos_entrega) ? v.fotos_entrega : null,
        createdAt: new Date(v.created_at),
        updatedAt: new Date(v.updated_at)
      }));
      
        set({
          ventas: ventasLocales,
          paginacion: { ...get().paginacion, total: ventasLocales.length }
        });

        return ventasLocales;
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al cargar ventas desde Supabase'
      }
    );
  },

  fetchVenta: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implementar llamada real a la API
      // const response = await ventasService.getVenta(id);
      
      const venta = get().ventas.find(v => v.id === id);
      if (venta) {
        set({ ventaSeleccionada: venta, isLoading: false });
      } else {
        set({ error: 'Venta no encontrada', isLoading: false });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al cargar venta',
        isLoading: false
      });
    }
  },

  crearVenta: async (venta: Partial<Venta>) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Creating venta:', venta);

        // Get current user from auth store
        const { useAuthStore } = await import('./useAuthStore');
        const authStore = useAuthStore.getState();
        const currentUser = authStore.usuario;

        const empresaId = getCurrentEmpresaIdSync();
        if (!currentUser || !empresaId || empresaId === 'sin-empresa') {
          throw new Error('Usuario no tiene empresa asignada');
        }

        logger.debug('👤 Creating venta for user empresa:', empresaId);

        const numeroVenta = await generateUniqueNumero('ventas', empresaId, venta.numero);

      // Convert to Supabase format (only use columns that exist in the database)
      const supabaseVenta = {
        empresa_id: empresaId,
        cliente_id: venta.clienteId || null,
        numero: numeroVenta,
        fecha: (venta.fecha || new Date()).toISOString(),
        subtotal: venta.subtotal || 0,
        impuestos: venta.impuestos || 0,
        descuentos: venta.descuentos || 0,
        total: venta.total || 0,
        metodo_pago: venta.metodoPago || null, // Fixed: Don't default to EFECTIVO
        // fecha_pago removed - column doesn't exist in ventas table
        estado: venta.estado || 'PENDIENTE',
        notas: venta.notas || '',
        rentabilidad_real: venta.rentabilidadReal || 0, // CRITICAL: Save profitability to database
        items: JSON.stringify(venta.items || []), // Store items as JSON
        ...(venta.canalOrigen ? { canal_origen: venta.canalOrigen } : {}),
        ...(venta.fotoEntrega != null && venta.fotoEntrega !== ''
          ? {
              foto_entrega: venta.fotoEntrega,
              fotos_entrega: venta.fotosEntrega?.length
                ? venta.fotosEntrega
                : [venta.fotoEntrega],
            }
          : {}),
      };

        logger.debug('🔍 VENTA TO CREATE:', supabaseVenta);

        // Create in Supabase
        const { data: createdVenta, error: createError } = await supabase
          .from('ventas')
          .insert(supabaseVenta)
          .select()
          .single();

        if (createError) {
          logger.error('🔍 SUPABASE CREATE ERROR DETAILS:', {
            message: createError.message,
            details: createError.details,
            hint: createError.hint,
            code: createError.code
          });
          logger.error('🔍 VENTA DATA THAT FAILED:', supabaseVenta);
          throw createError;
        }

        logger.debug('✅ Venta creada en Supabase:', createdVenta);
      
      // Convert back to local format
      const nuevaVenta: Venta = {
        id: createdVenta.id,
        numero: createdVenta.numero,
        clienteId: createdVenta.cliente_id,
        cliente: venta.cliente || undefined, // Preserve cliente object if provided in venta data
        fecha: new Date(createdVenta.fecha),
        items: venta.items || [], // Items are preserved with producto names from venta data
        subtotal: createdVenta.subtotal || 0,
        impuestos: createdVenta.impuestos || 0,
        descuentos: createdVenta.descuentos || 0,
        total: createdVenta.total || 0,
        metodoPago: createdVenta.metodo_pago || '', // Fixed: Don't default to EFECTIVO
        estado: createdVenta.estado || 'PENDIENTE',
        vendedor: 'Vendedor Demo', // Default value since column doesn't exist
        notas: createdVenta.notas || '',
        rentabilidadReal: createdVenta.rentabilidad_real || 0, // Use actual value from database
        canalOrigen: (createdVenta as { canal_origen?: string }).canal_origen,
        fotoEntrega: createdVenta.foto_entrega ?? null,
        fotosEntrega: Array.isArray(createdVenta.fotos_entrega) ? createdVenta.fotos_entrega : null,
        createdAt: new Date(createdVenta.created_at),
        updatedAt: new Date(createdVenta.updated_at)
      };


        set(state => ({
          ventas: [...state.ventas, nuevaVenta]
        }));

        logger.debug('✅ Venta creada exitosamente:', nuevaVenta);

        return nuevaVenta;
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al crear venta en Supabase'
      }
    );
  },

  actualizarVenta: async (id: string, venta: Partial<Venta>) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Updating venta:', id, venta);
      
      // Convert to Supabase format (DB columns are snake_case)
      const supabaseUpdates: any = {};
      if (venta.numero !== undefined) supabaseUpdates.numero = venta.numero;
      if (venta.clienteId !== undefined) supabaseUpdates.cliente_id = venta.clienteId;
      if (venta.fecha !== undefined) supabaseUpdates.fecha = venta.fecha.toISOString();
      if (venta.subtotal !== undefined) supabaseUpdates.subtotal = venta.subtotal;
      if (venta.impuestos !== undefined) supabaseUpdates.impuestos = venta.impuestos;
      if (venta.descuentos !== undefined) supabaseUpdates.descuentos = venta.descuentos;
      if (venta.total !== undefined) supabaseUpdates.total = venta.total;
      if (venta.metodoPago !== undefined) supabaseUpdates.metodo_pago = venta.metodoPago;
      if (venta.estado !== undefined) supabaseUpdates.estado = venta.estado;
      if (venta.vendedor !== undefined) supabaseUpdates.vendedor = venta.vendedor;
      if (venta.notas !== undefined) supabaseUpdates.notas = venta.notas;
        if (venta.rentabilidadReal !== undefined) supabaseUpdates.rentabilidad_real = venta.rentabilidadReal;
        if (venta.canalOrigen !== undefined) supabaseUpdates.canal_origen = venta.canalOrigen;
        if (venta.fotoEntrega !== undefined) supabaseUpdates.foto_entrega = venta.fotoEntrega;
        if (venta.fotosEntrega !== undefined) supabaseUpdates.fotos_entrega = venta.fotosEntrega;

        logger.debug('🔍 VENTA UPDATES:', supabaseUpdates);

        // Update in Supabase
        const { data: updatedVenta, error: updateError } = await supabase
          .from('ventas')
          .update(supabaseUpdates)
          .eq('id', id)
          .select()
          .maybeSingle();

        if (updateError) {
          logger.error('🔍 SUPABASE UPDATE ERROR DETAILS:', {
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint,
            code: updateError.code
          });
          throw updateError;
        }
        if (!updatedVenta) {
          throw new Error('Venta no encontrada al actualizar');
        }

        logger.debug('✅ Venta actualizada en Supabase:', updatedVenta);
      
      // Convert back to local format (Supabase returns snake_case)
      const ventaLocal: Venta = {
        id: updatedVenta.id,
        numero: updatedVenta.numero,
        clienteId: updatedVenta.cliente_id ?? updatedVenta.clienteId,
        fecha: new Date(updatedVenta.fecha),
        items: venta.items || [],
        subtotal: updatedVenta.subtotal || 0,
        impuestos: updatedVenta.impuestos || 0,
        descuentos: updatedVenta.descuentos || 0,
        total: updatedVenta.total || 0,
        metodoPago: updatedVenta.metodo_pago || '', // Fixed: Don't default to EFECTIVO
        estado: updatedVenta.estado || 'PENDIENTE',
        vendedor: updatedVenta.vendedor || 'Vendedor Demo',
        notas: updatedVenta.notas || '',
        rentabilidadReal: updatedVenta.rentabilidad_real || 0,
        canalOrigen: (updatedVenta as { canal_origen?: string }).canal_origen,
        fotoEntrega: updatedVenta.foto_entrega ?? null,
        fotosEntrega: Array.isArray(updatedVenta.fotos_entrega) ? updatedVenta.fotos_entrega : null,
        createdAt: new Date(updatedVenta.created_at),
        updatedAt: new Date(updatedVenta.updated_at)
      };


        // Update local state
        const updatedVentas = get().ventas.map(v =>
          v.id === id ? ventaLocal : v
        );

        set(state => ({
          ventas: updatedVentas,
          ventaSeleccionada: state.ventaSeleccionada?.id === id ? ventaLocal : state.ventaSeleccionada
        }));

        logger.debug('✅ Venta actualizada exitosamente:', id);

        return ventaLocal;
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al actualizar venta en Supabase'
      }
    );
  },

  anularVenta: async (id: string) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Anulando venta:', id);

        // Get the venta first to restore inventory
        const ventaToCancel = get().ventas.find(v => v.id === id);
        if (!ventaToCancel) {
          throw new Error('Venta no encontrada');
        }

        // Update in Supabase
        const { data: updatedVenta, error: updateError } = await supabase
          .from('ventas')
          .update({ estado: 'ANULADA' })
          .eq('id', id)
          .select()
          .maybeSingle();

        if (updateError) {
          logger.error('🔍 SUPABASE ANULAR ERROR DETAILS:', {
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint,
            code: updateError.code
          });
          throw updateError;
        }
        if (!updatedVenta) {
          throw new Error('Venta no encontrada al anular');
        }

        logger.debug('✅ Venta anulada en Supabase:', updatedVenta);
      
      // Convert back to local format (Supabase returns snake_case)
      const ventaLocal: Venta = {
        id: updatedVenta.id,
        numero: updatedVenta.numero,
        clienteId: updatedVenta.cliente_id ?? updatedVenta.clienteId,
        fecha: new Date(updatedVenta.fecha),
        items: [],
        subtotal: updatedVenta.subtotal || 0,
        impuestos: updatedVenta.impuestos || 0,
        descuentos: updatedVenta.descuentos || 0,
        total: updatedVenta.total || 0,
        metodoPago: updatedVenta.metodo_pago || '', // Fixed: Don't default to EFECTIVO
        estado: 'ANULADA',
        vendedor: updatedVenta.vendedor || 'Vendedor Demo',
        notas: updatedVenta.notas || '',
        rentabilidadReal: updatedVenta.rentabilidad_real || 0,
        fotoEntrega: updatedVenta.foto_entrega ?? null,
        fotosEntrega: Array.isArray(updatedVenta.fotos_entrega) ? updatedVenta.fotos_entrega : null,
        createdAt: new Date(updatedVenta.created_at),
        updatedAt: new Date(updatedVenta.updated_at)
      };


        // Update local state
        const updatedVentas = get().ventas.map(v =>
          v.id === id ? ventaLocal : v
        );

        set(state => ({
          ventas: updatedVentas,
          ventaSeleccionada: state.ventaSeleccionada?.id === id ? ventaLocal : state.ventaSeleccionada
        }));

        // CRITICAL: Restore inventory for cancelled items
        logger.debug('📦 Restoring inventory for cancelled sale...');
        const { useProductosStore } = await import('./useProductosStore');
        const productosStore = useProductosStore.getState();

        if (ventaToCancel.items && ventaToCancel.items.length > 0) {
          const itemsToRestore = ventaToCancel.items
            .filter(item => item.productoId) // Only items with valid product IDs
            .map(item => ({
              productoId: item.productoId,
              cantidad: item.cantidad
            }));

          if (itemsToRestore.length > 0) {
            await productosStore.ajustarStockMultiple(itemsToRestore, 'ENTRADA');
            logger.debug(`✅ Inventory restored for ${itemsToRestore.length} products`);
          }
        }

        logger.debug('✅ Venta anulada exitosamente:', id);
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al anular venta en Supabase'
      }
    );
  },

  eliminarVenta: async (id: string) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Deleting venta:', id);

        // Delete from Supabase
        const { error: deleteError } = await supabase
          .from('ventas')
          .delete()
          .eq('id', id);

        if (deleteError) {
          logger.error('🔍 SUPABASE DELETE ERROR DETAILS:', {
            message: deleteError.message,
            details: deleteError.details,
            hint: deleteError.hint,
            code: deleteError.code
          });
          throw deleteError;
        }

        logger.debug('✅ Venta eliminada de Supabase:', id);

        // Remove from local state
        const updatedVentas = get().ventas.filter(v => v.id !== id);

        set(state => ({
          ventas: updatedVentas,
          ventaSeleccionada: state.ventaSeleccionada?.id === id ? null : state.ventaSeleccionada
        }));

        logger.debug('✅ Venta eliminada exitosamente:', id);
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al eliminar venta en Supabase'
      }
    );
  },

  agregarItemVenta: (itemData: { productoId: string; producto: Producto; cantidad: number; precioUnitario: number; descuento: number; total: number; costoUnitario: number; ivaPorcentaje: number }) => {
    const ventaEnProceso = get().ventaEnProceso;
    if (!ventaEnProceso) {
      // Crear nueva venta en proceso
      const nuevaVenta: Venta = {
        id: Date.now().toString(),
        numero: '',
        clienteId: '',
        fecha: new Date(),
        items: [],
        subtotal: 0,
        impuestos: 0,
        descuentos: 0,
        total: 0,
        metodoPago: '', // Fixed: Don't default to EFECTIVO
        estado: 'PENDIENTE',
        vendedor: 'Vendedor Demo',
        notas: '',
        rentabilidadReal: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      set({ ventaEnProceso: nuevaVenta });
    }

    const item: any = {
      id: Date.now().toString(),
      ...itemData
    };

    set(state => ({
      ventaEnProceso: state.ventaEnProceso ? {
        ...state.ventaEnProceso,
        items: [...state.ventaEnProceso.items, item]
      } : null
    }));

    get().calcularTotales();
  },

  removerItemVenta: (productoId: string) => {
    set(state => ({
      ventaEnProceso: state.ventaEnProceso ? {
        ...state.ventaEnProceso,
        items: state.ventaEnProceso.items.filter(item => item.productoId !== productoId)
      } : null
    }));

    get().calcularTotales();
  },

  actualizarItemVenta: (productoId: string, cantidad: number, precio?: number) => {
    set(state => ({
      ventaEnProceso: state.ventaEnProceso ? {
        ...state.ventaEnProceso,
        items: state.ventaEnProceso.items.map(item => {
          if (item.productoId === productoId) {
            const precioVenta = precio || item.precioUnitario;
            return {
              ...item,
              cantidad: cantidad,
              precioUnitario: precioVenta,
              total: precioVenta * cantidad
            };
          }
          return item;
        })
      } : null
    }));

    get().calcularTotales();
  },

  calcularTotales: () => {
    const ventaEnProceso = get().ventaEnProceso;
    if (!ventaEnProceso) return { subtotal: 0, impuestos: 0, total: 0, rentabilidadReal: 0 };

    // Calculate IVA based on each product's actual IVA rate from database
    let subtotalSinIVA = 0;
    let impuestos = 0;
    let costoTotal = 0;
    let ventaTotal = 0;
    
    ventaEnProceso.items.forEach(item => {
      const itemIVAPorcentaje = item.ivaPorcentaje || 0; // Use product's actual IVA from database
      const itemSubtotalSinIVA = item.total / (1 + itemIVAPorcentaje / 100);
      const itemIVA = item.total - itemSubtotalSinIVA;
      
      subtotalSinIVA += itemSubtotalSinIVA;
      impuestos += itemIVA;
      
      // Calculate profitability: (sale price - cost) * quantity
      const costoItem = (item.costoUnitario || 0) * item.cantidad;
      const ventaItem = item.total;
      costoTotal += costoItem;
      ventaTotal += ventaItem;
    });
    
    const totalConIVA = ventaEnProceso.items.reduce((sum, item) => sum + item.total, 0);
    const totalConDescuentos = totalConIVA - (ventaEnProceso.descuentos || 0); // Apply discounts first
    const total = totalConDescuentos; // Total is the discounted IVA-inclusive amount
    
    // Calculate profitability: (Total Sales - Total Cost) = Profit
    const rentabilidadReal = ventaTotal - costoTotal;

    set(state => ({
      ventaEnProceso: state.ventaEnProceso ? {
        ...state.ventaEnProceso,
        subtotal: subtotalSinIVA,
        impuestos: impuestos,
        total: total,
        rentabilidadReal: rentabilidadReal
      } : null
    }));

    return { subtotal: subtotalSinIVA, impuestos, total, rentabilidadReal };
  },

  /**
   * Venta rápida (POS cart) → ventas table. Stock SALIDA here only.
   * Factura flows use convertirFacturaAVenta / GestionFacturas (crearVenta + their own SALIDA).
   * Do not add stock deduction to crearVenta — that would double-deduct on factura conversion.
   */
  finalizarVenta: async (ventaData: { clienteId: string; metodoPago: string; notas?: string }) => {
    const ventaEnProceso = get().ventaEnProceso;
    if (!ventaEnProceso || ventaEnProceso.items.length === 0) {
      set({ error: 'No hay items en la venta' });
      return;
    }

    try {
      // Calculate final totals including profitability
      const totales = get().calcularTotales();

      if (!totales) {
        set({ error: 'Error al calcular totales de la venta' });
        return;
      }

      const ventaFinalizada: Venta = {
        ...ventaEnProceso,
        clienteId: ventaData.clienteId,
        metodoPago: ventaData.metodoPago as any,
        estado: 'PAGADA',
        notas: ventaData.notas,
        rentabilidadReal: totales.rentabilidadReal,
        updatedAt: new Date()
      };

      await get().crearVenta(ventaFinalizada);

      const itemsStock = ventaEnProceso.items.filter((item) => {
        if (!item.productoId) return false;
        const prod = typeof item.producto === 'object' && item.producto ? item.producto : null;
        return (prod?.inventory_type ?? 'tracked') !== 'unlimited';
      });
      if (itemsStock.length > 0 && ventaEnProceso.empresaId) {
        const empresaId = ventaEnProceso.empresaId;
        const { useProductosStore } = await import('./useProductosStore');
        await useProductosStore.getState().fetchProductos(empresaId);
        await useProductosStore.getState().ajustarStockMultiple(
          itemsStock.map((item) => ({
            productoId: item.productoId,
            cantidad: item.cantidad,
          })),
          'SALIDA'
        );
      }

      set({ ventaEnProceso: null });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error al finalizar venta'
      });
    }
  },

  setVentaSeleccionada: (venta) => {
    set({ ventaSeleccionada: venta });
  },

  setVentaEnProceso: (venta) => {
    set({ ventaEnProceso: venta });
  },

  setFiltros: (filtros) => {
    const next = { ...get().filtros, ...filtros };
    set({ filtros: next });
    void get().fetchVentas(next);
  },

  setPaginacion: (paginacion) => {
    set({ paginacion: { ...get().paginacion, ...paginacion } });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      ventas: [],
      ventaSeleccionada: null,
      ventaEnProceso: null,
      isLoading: false,
      error: null,
      filtros: { estado: '', fechaDesde: '', fechaHasta: '', clienteId: '', metodoPago: '' },
      paginacion: { pagina: 1, porPagina: 20, total: 0 },
    });
  },

  limpiarVentaEnProceso: () => {
    set({ ventaEnProceso: null });
  },

  obtenerVentaEnProceso: () => {
    return get().ventaEnProceso;
  },

  obtenerClienteVenta: (_clienteId: string) => {
    // TODO: Implementar búsqueda real de cliente
    return null;
  },

  obtenerProductoVenta: (_productoId: string) => {
    // TODO: Implementar búsqueda real de producto
    return null;
  },

  crearClienteRapido: async (datosCliente: Partial<Cliente>) => {
    // TODO: Implementar creación real de cliente
    const nuevoCliente: Cliente = {
      id: Date.now().toString(),
      nombre: datosCliente.nombre || 'Cliente Nuevo',
              tipoDocumento: datosCliente.tipoDocumento || 'CC',
        numeroDocumento: datosCliente.numeroDocumento || '',
      telefono: datosCliente.telefono || '',
      email: datosCliente.email || '',
      direccion: datosCliente.direccion || '',
      ciudad: datosCliente.ciudad || '',
      barrio: datosCliente.barrio || '',
      createdAt: new Date(),
      ultimaCompra: new Date(),
      totalCompras: 0,
      rentabilidadTotal: 0,
      promedioCompra: 0,
      frecuenciaCompra: 0,
      riesgoChurn: 'BAJO',
      segmento: datosCliente.segmento || 'REGULAR',
      metodoPagoPreferido: datosCliente.metodoPagoPreferido || 'EFECTIVO',
      canalPreferido: datosCliente.canalPreferido || 'PRESENCIAL',
      activo: true,
      notas: datosCliente.notas || '',
      empresaId: 'empresa1',
      updatedAt: new Date()
    };
    return nuevoCliente;
  }
}));
